/**
 * LocationDevices — Módulo "Localização / Dispositivos".
 *
 * Recebe toda a área de mapa + canais de origem + dispositivos que
 * anteriormente vivia no Dashboard. Consome /api/dashboard/widgets
 * (mesma fonte de dados) com filtro de campanha + métrica ativa.
 */

import { useEffect, useState } from 'react'
import Layout from '../components/Layout.jsx'
import BowtieVisual from '../components/BowtieVisual.jsx'
import CampaignFilter from '../components/CampaignFilter.jsx'
import MetricCards from '../components/MetricCards.jsx'
import KpiCard from '../components/KpiCard.jsx'
import PageState from '../components/PageState.jsx'
import PageFooter from '../components/PageFooter.jsx'
import LeadsMap from '../components/LeadsMap.jsx'
import SEOBreakdowns from '../components/SEOBreakdowns.jsx'
import Icon from '../components/icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import { useViewMode } from '../hooks/useViewMode.js'
import { useCampaignFilter, setAvailableCampaigns } from '../hooks/useCampaignFilter.js'
import { useDashboard } from '../hooks/useDashboard.js'
import { useDashboardWidgets } from '../hooks/useDashboardWidgets.js'
import { getCampaigns } from '../db/api.js'
import { DEFAULT_METRIC, METRIC_LABEL } from '../lib/metricFilters.js'

// Métricas sem dados geográficos confiáveis: o mapa não deve renderizar
// nenhuma informação regional, apenas a mensagem informativa.
const NO_GEO_METRICS = new Set(['investment', 'impressions', 'clicks'])

function MapUnavailableNotice({ metricLabel, children }) {
  return (
    <div className="bw-panel bw-map-panel">
      <div className="bw-panel-head">
        <Icon n="link" s={14} />
        <span>Origem geográfica · {metricLabel}</span>
      </div>
      <div className="bw-map-unavailable">
        <Icon n="alert" s={22} />
        <p className="bw-map-unavailable-title">
          Dados por região não estão disponíveis.
        </p>
      </div>
      {children}
    </div>
  )
}

export default function LocationDevices() {
  const { mode } = useViewMode()
  const { period, selectedClient } = useApp()
  const { selectedIds, count, total, isFiltered, isAllSelected, selectAll } = useCampaignFilter()
  const [activeMetric, setActiveMetric] = useState(DEFAULT_METRIC)
  const [campaignsLoading, setCampaignsLoading] = useState(true)

  // Carrega campanhas reais p/ habilitar CampaignFilter no topbar.
  useEffect(() => {
    let cancelled = false
    async function load() {
      setCampaignsLoading(true)
      try {
        const campaigns = await getCampaigns()
        if (!cancelled) {
          const normalized = (campaigns || []).map((c) => ({
            id: c.id || c.google_campaign_id || String(c.campaign_id),
            name: c.name || c.campaign_name || 'Campanha',
            source: c.channel?.includes('SEARCH') ? 'Google Ads' : 'Meta Ads',
            status: (c.status || 'ENABLED').toLowerCase() === 'enabled' ? 'active' : 'paused',
          }))
          setAvailableCampaigns(normalized)
        }
      } catch (err) {
        console.error('[LocationDevices] erro ao carregar campanhas:', err)
      } finally {
        if (!cancelled) setCampaignsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [selectedClient?.id])

  const campaignIds = isAllSelected ? null : selectedIds

  const { data, loading: dataLoading, error: dataError } =
    useDashboard(period.start, period.end, campaignIds, selectedClient?.id)

  const { data: widgets, loading: widgetsLoading, error: widgetsError, refetch } =
    useDashboardWidgets(period.start, period.end, campaignIds, selectedClient?.id, activeMetric)

  const loading = campaignsLoading || dataLoading
  const error = dataError || widgetsError

  const campaigns = widgets?.campaigns ?? []
  const devices = widgets?.devices ?? []
  const pages = widgets?.pages ?? []
  const cities = widgets?.cities ?? []
  const leadsGeo = widgets?.leadsGeo ?? []
  const leadsGeoMeta = widgets?.leadsGeoMeta ?? null
  // Backend marca geoAvailable=false p/ investment/impressions/clicks: a tabela
  // google_ads_location replica o total da campanha em cada cidade, sem split
  // geográfico real. Nesses casos ocultamos o mapa (ver dashboard.py).
  const geoAvailable = widgets?.geoAvailable !== false && !NO_GEO_METRICS.has(activeMetric)
  const metricLabel = METRIC_LABEL[activeMetric] || activeMetric
  const isEmpty = count === 0 && total > 0

  // Total do período por métrica (o resumo não traz Clicks Whats, Formulários e
  // Contatos: esses só mostram o total quando selecionados, via widgets).
  const metricValues = {
    ...(data?.metrics || {}),
    ...(leadsGeoMeta?.total != null && geoAvailable ? { [activeMetric]: leadsGeoMeta.total } : {}),
  }
  const metricCards = (
    <MetricCards
      active={activeMetric}
      onChange={setActiveMetric}
      values={metricValues}
      disabled={widgetsLoading || campaignsLoading}
    />
  )

  const kpisBase = data
    ? [
        data.kpis.investment,
        data.kpis.revenue,
        data.kpis.roas,
        data.kpis.cac,
      ].map((k, i) => ({ ...k, key: ['investment', 'revenue', 'roas', 'cac'][i] }))
    : []

  return (
    <Layout
      breadcrumb={[{ label: 'Marketing' }, { label: 'Google Ads', to: '/google-ads' }, { label: 'Localização / Dispositivos' }]}
      title="Google Ads — Localização / Dispositivos"
      showPeriod
    >
      <div className="ga-filters-bar">
        <div className="ga-filters-left">
          <CampaignFilter />
        </div>
      </div>
      <div className="dash-shell">
        {isFiltered && !isEmpty && !loading && (
          <div className="dash-filter-banner">
            <Icon n="filter" s={12} />
            <span>
              Mostrando dados de <strong>{count} de {total} campanhas</strong>
            </span>
            <button className="dash-filter-clear" onClick={selectAll} type="button">
              Ver todas
            </button>
          </div>
        )}

        <PageState loading={loading} error={error} onRetry={refetch} />

        {!loading && isEmpty && (
          <div className="dash-empty">
            <div className="dash-empty-icon"><Icon n="filter" s={20} /></div>
            <h2 className="dash-empty-title">Nenhuma campanha selecionada</h2>
            <p className="dash-empty-desc">
              Selecione ao menos uma campanha no filtro do topo para ver localização e dispositivos.
            </p>
            <button className="dash-empty-cta" onClick={selectAll} type="button">
              Ver todas as campanhas
            </button>
          </div>
        )}

        {!loading && !error && !isEmpty && data && (
          <>
            {/* Grid principal: mapa na largura toda e, abaixo, os 4 cards
                (campanhas, páginas, dispositivos, cidades) lado a lado. */}
            <style>{`
              .gads-locdev-grid{grid-template-columns:1fr;align-items:stretch}
              .gads-locdev-grid > .bw-map-panel{height:clamp(460px,68vh,760px)}
              .gads-locdev-grid > .seo-breakdowns-grid{height:auto;grid-template-columns:repeat(4,minmax(0,1fr));grid-template-rows:none;grid-auto-rows:300px}
              /* svg escala p/ caber no wrap (contain), sem transbordar o card.
                 O tamanho real vem do style inline em LeadsMap.jsx (width:100%
                 + aspect-ratio) — isso aqui só evita vazar do card. */
              .gads-locdev-grid .bw-map-wrap{overflow:hidden}
              /* Cabeçalho, legenda e aviso sobrepostos ao mapa (sem divisórias
                 internas), p/ o mapa ocupar todo o card. */
              .gads-locdev-grid > .bw-map-panel{position:relative}
              .gads-locdev-grid > .bw-map-panel .bw-panel-head{position:absolute;top:0;left:0;right:0;z-index:1;padding:16px 18px;border-bottom:0;pointer-events:none}
              .gads-locdev-grid .bw-map-wrap{padding:0}
              .gads-locdev-grid .bw-map-legend{position:absolute;bottom:12px;left:50%;transform:translateX(-50%);width:max-content;z-index:1;padding:6px 12px;border-top:0;border-radius:100px;background:rgba(255,255,255,.7);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);pointer-events:none}
              .gads-locdev-grid .bw-map-note{position:absolute;bottom:58px;left:0;right:0;z-index:1;justify-content:center;border-top:0;pointer-events:none}
              @media(max-width:1280px){
                .gads-locdev-grid > .seo-breakdowns-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
              }
              @media(max-width:1024px){
                .gads-locdev-grid > .bw-map-panel{height:clamp(360px,60vh,520px)}
              }
              @media(max-width:640px){
                .gads-locdev-grid > .seo-breakdowns-grid{grid-template-columns:minmax(0,1fr)}
              }
            `}</style>
            <div className={`seo-main-grid gads-locdev-grid ${widgetsLoading ? 'is-loading' : ''}`}>
              {geoAvailable
                ? <LeadsMap data={leadsGeo} metricLabel={metricLabel} meta={leadsGeoMeta} zoomable>{metricCards}</LeadsMap>
                : <MapUnavailableNotice metricLabel={metricLabel}>{metricCards}</MapUnavailableNotice>}
              <SEOBreakdowns
                sources={campaigns}
                devices={devices}
                pages={pages}
                cities={cities}
                sourcesTitle="Campanhas"
                sourcesIcon="ads"
                loading={widgetsLoading}
              />
            </div>

            <div className="kpi-grid kpi-grid-4">
              {kpisBase.map((k) => (
                <KpiCard key={k.key} kpi={k} mode={mode} />
              ))}
            </div>

            <BowtieVisual data={data.funnel} />

            <PageFooter text="Widgets atualizados conforme métrica selecionada" onRefresh={refetch} />
          </>
        )}
      </div>

    </Layout>
  )
}
